import AIChatMessageCard from '@/components/aichat/AIChatMessageCard';
import AIPromptInput from '@/components/editor/ai/AIPromptInput';
import AIStatusBanner from '@/components/editor/ai/AIStatusBanner';
import AIGeneratingIndicator from '@/components/aichat/AIGeneratingIndicator';
import { apolloClient } from '@/graphql';
import useAIAssist, { AIStatus } from '@/hooks/useAIAssist';
import { AiChatThread } from '@saga/api';
import { AIMessage, AISourceItem, AIFunctionName } from '@saga/shared';
import { ReactEditor } from 'slate-react';
import React from 'react';
import * as api from '@saga/api';
import { useCurrentWorkspace } from '@/components/WorkspaceContext';
import { useUserContext } from '@/components/UserContext';
import { useTranslation } from 'react-i18next';
import { useSpace } from '@/components/SpaceProvider';
import AISourcesList from '@/components/editor/ai/AISourcesList';
import { getPromptText, getReferencedDocuments } from '@/utils/aiUtils';
import { track } from '@/analytics';
import { v4 as uuid } from 'uuid';
import { useMountedEditors } from '../../../../shared/src/Editor';
import classNames from 'classnames';
import ScrollToBottomButton from './ScrollToBottomButton';
import { useAIToolCalls, isToolError, requiresConfirmation } from '@/hooks/useAIToolCalls';
import AIToolConfirmationCard, {
    CreatePageData,
    CreatePageWithContentData,
    RewritePageContentData,
} from './AIToolConfirmationCard';

type Props = {
    thread: AiChatThread;
    mode: 'pane' | 'sidebar';
};

type PendingConfirmation =
    | ({
          type: 'create_page';
          data: CreatePageData;
      } & { id: string })
    | ({
          type: 'create_page_with_content';
          data: CreatePageWithContentData;
      } & { id: string })
    | ({
          type: 'rewrite_page_content';
          data: RewritePageContentData;
      } & { id: string });

const AIChatScreen = ({ thread, mode }: Props) => {
    const [history, setHistory] = React.useState<AIMessage[]>([]);

    React.useEffect(() => {
        setHistory(
            thread.messages.map((m) => ({
                content: m.content ?? null,
                tool_call_id: m.tool_call_id,
                tool_calls: m.tool_calls,
                role: m.role,
                id: uuid(),
            })),
        );
        // Clear pending confirmations when thread changes
        setPendingConfirmations([]);
    }, [thread]);
    const { t } = useTranslation();
    const [text, setText] = React.useState<string>('');
    const [sources, setSources] = React.useState<AISourceItem[]>([]);
    const { urlKey, id: spaceId } = useCurrentWorkspace();
    const { space } = useSpace();

    const { user } = useUserContext();
    const { status: responseStatus, messages, generateAIText, stop, error } = useAIAssist(history, thread.id);
    const [status, setStatus] = React.useState<AIStatus>(responseStatus);

    const mountedEditors = useMountedEditors();

    const [scrollStick, setScrollStick] = React.useState(true);
    const [pendingConfirmations, setPendingConfirmations] = React.useState<PendingConfirmation[]>([]);

    const scrollRef = React.useRef<HTMLDivElement>(null);
    const ref = React.useRef<HTMLDivElement>(null);

    const inputId = React.useMemo(() => uuid(), []);

    // Show generating indicator when loading and latest message is a user message or empty assistant message
    const showGenerating = React.useMemo(() => {
        if (status !== 'loading') return false;

        const latestMessage = messages[0];
        return !latestMessage || latestMessage.role === 'user' || !latestMessage.content?.trim();
    }, [messages[0], status]);

    const onSend = React.useCallback(
        async (text: string, sources: AISourceItem[]) => {
            const id = uuid();
            track('ai-chat-message-sent', { source: mode });
            setText('');

            // If AI is currently generating, stop it first
            if (status === 'loading' && stop) {
                try {
                    await stop();
                } catch (error) {
                    console.error('Error stopping AI generation:', error);
                }
            }

            generateAIText({ prompt: text, references: sources }, id);
            setHistory([{ content: `<instructions>${text}</instructions>`, role: 'user', id }, ...history]);
        },
        [generateAIText, history, mode, status, stop],
    );

    const storeInCache = React.useCallback(
        (messages: AIMessage[]) => {
            const cache = apolloClient.cache.readQuery<api.ChatThreadsQuery>({
                query: api.ChatThreadsDocument,
                variables: { input: { urlKey } },
            });
            const cachedThreads = cache?.chatThreads ?? [];

            const mappedMessages: api.AiChatMessage[] = messages.map((message) => ({
                __typename: 'AIChatMessage',
                content: message.content,
                createdAt: new Date().toISOString(),
                role: message.role as api.AiChatRole,
                tool_call_id: message.tool_call_id ?? null,
                tool_calls: message.tool_calls ?? null,
            }));

            apolloClient.writeQuery({
                query: api.ChatThreadsDocument,
                data: {
                    ...cache,
                    chatThreads: cachedThreads.some((cached) => cached.id === thread.id)
                        ? cachedThreads.map((cached) =>
                              cached.id === thread.id ? { ...cached, messages: mappedMessages } : cached,
                          )
                        : [
                              {
                                  __typename: 'AIChatThread',
                                  id: thread.id,
                                  userId: user?.id ?? '',
                                  spaceId,
                                  messages: mappedMessages,
                              },
                              ...cachedThreads,
                          ],
                },
                variables: { input: { urlKey } },
            });
        },
        [spaceId, thread.id, urlKey, user],
    );

    const focusInputField = React.useCallback(() => {
        const element = document.getElementById(inputId);
        if (element instanceof HTMLElement) {
            setTimeout(() => element.focus());
        }
    }, [inputId]);

    const isEmpty = React.useMemo(
        () => thread.messages.length === 0 && history.length === 0,
        [thread.messages.length, history.length],
    );

    const messagesWithSources = React.useMemo(
        () =>
            messages
                .flatMap((message, i) => {
                    if (message.role !== 'user' && (message.role !== 'assistant' || message.tool_calls)) return [];

                    // Find all tool calls related to this message
                    let toolCalls: AIMessage[] = [];
                    for (let index = i + 1; index < messages.length; index++) {
                        const message = messages[index];
                        if (message.role === 'assistant' && !message.tool_calls) break;
                        if (message.role === 'assistant' && message.tool_calls) toolCalls.push(message);
                    }
                    return { message, sources: getReferencedDocuments(toolCalls, space) };
                })
                .reverse(),
        [messages, space],
    );

    React.useLayoutEffect(() => {
        focusInputField();
    }, [thread.id, focusInputField]);

    React.useEffect(() => {
        if (!ref.current) return;
        const resizeObserver = new ResizeObserver(() => {
            scrollStick && scrollRef.current?.scrollTo({ top: ref.current?.scrollHeight });
        });
        resizeObserver.observe(ref.current);
        return () => resizeObserver.disconnect();
    }, [ref, scrollStick, isEmpty]);

    React.useEffect(() => {
        setStatus(responseStatus);
    }, [responseStatus]);

    React.useEffect(() => {
        if (status === 'done') {
            setHistory(messages);
            if (![...mountedEditors].some(ReactEditor.isFocused)) focusInputField();
        }
    }, [messages, status, mountedEditors, focusInputField]);

    React.useEffect(() => {
        status === 'done' && messages.length && storeInCache(messages);
    }, [messages, storeInCache, status]);

    React.useEffect(() => {
        const handleEscapeKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && status === 'loading') {
                e.preventDefault();
                track('esc-button-ai-stop-generation', { source: mode });
                stop?.();
            }
        };

        document.addEventListener('keydown', handleEscapeKey);
        return () => document.removeEventListener('keydown', handleEscapeKey);
    }, [status, stop, mode]);

    // Handle AI tool calls
    const handleToolCall = React.useCallback((toolName: AIFunctionName, toolCall: any, response: any) => {
        // Handle different tools based on their name
        switch (toolName) {
            case AIFunctionName.createPage:
                if (response.action === 'create_page') {
                    if (isToolError(response)) {
                        console.error('Create page error:', response.message);
                        // TODO: Show error toast to user
                    } else if (requiresConfirmation(response)) {
                        setPendingConfirmations((prev) => [
                            ...prev,
                            {
                                id: uuid(),
                                type: 'create_page',
                                data: { title: response.title || 'Untitled Page' },
                            },
                        ]);
                        track('ai-create-page-dialog-shown');
                    }
                }
                break;

            case AIFunctionName.createPageWithContent:
                if (response.action === 'create_page_with_content') {
                    if (isToolError(response)) {
                        console.error('Create page with content error:', response.message);
                        // TODO: Show error toast to user
                    } else if (requiresConfirmation(response)) {
                        setPendingConfirmations((prev) => [
                            ...prev,
                            {
                                id: uuid(),
                                type: 'create_page_with_content',
                                data: {
                                    title: response.title || 'Untitled Page',
                                    content: response.content || '',
                                },
                            },
                        ]);
                        track('ai-create-page-with-content-dialog-shown');
                    }
                }
                break;

            case AIFunctionName.rewritePageContent:
                if (response.action === 'rewrite_page_content') {
                    if (isToolError(response)) {
                        console.error('Rewrite page content error:', response.message);
                        // TODO: Show error toast to user
                    } else if (requiresConfirmation(response)) {
                        setPendingConfirmations((prev) => [
                            ...prev,
                            {
                                id: uuid(),
                                type: 'rewrite_page_content',
                                data: {
                                    pageId: response.pageId,
                                    content: response.content || '',
                                },
                            },
                        ]);
                        track('ai-rewrite-page-content-dialog-shown');
                    }
                }
                break;

            // Add more tool handlers here as they are implemented
            case AIFunctionName.getDocumentContext:
            case AIFunctionName.getSpaceContext:
                // These tools don't require user interaction
                break;

            default:
                console.warn(`Unhandled tool call: ${toolName}`);
        }
    }, []);

    // Use the hook to detect tool calls
    useAIToolCalls({
        messages,
        status,
        history,
        onToolCall: handleToolCall,
    });

    // Auto-scroll when confirmations appear
    React.useEffect(() => {
        if (pendingConfirmations.length > 0 && scrollRef.current) {
            setTimeout(() => {
                scrollRef.current?.scrollTo({
                    top: scrollRef.current.scrollHeight,
                    behavior: 'smooth',
                });
            }, 100);
        }
    }, [pendingConfirmations.length]);

    return (
        <div
            className={classNames('relative flex flex-col overflow-hidden', {
                'justify-center h-3/4': isEmpty,
                'h-full': !isEmpty,
            })}
        >
            {!isEmpty ? (
                <div
                    ref={scrollRef}
                    role="group"
                    aria-label="Chat messages"
                    className="flex flex-1 flex-col overflow-y-auto px-3 select-text hide-scrollbar pb-5"
                    onScroll={(e) => {
                        const threshold = 10; // Threshold for considering the user at the bottom
                        const atBottom =
                            Math.abs(
                                e.currentTarget.clientHeight +
                                    e.currentTarget.scrollTop -
                                    (scrollRef.current?.scrollHeight ?? 0),
                            ) < threshold;
                        setScrollStick(atBottom);
                    }}
                >
                    <div ref={ref}>
                        <div className="flex-grow"></div>
                        {messagesWithSources.map((item, index) => {
                            // Correctly identify the last message after array reversal
                            const isLastMessage = index === messagesWithSources.length - 1;

                            // Determine if this specific assistant message is the one currently streaming
                            const isCurrentAssistantMessageStreaming =
                                status === 'loading' && item.message.role === 'assistant' && isLastMessage;

                            // A message is complete if it's a user message,
                            // or if it's an assistant message that is NOT currently streaming.
                            const isComplete =
                                item.message.role === 'user' ||
                                (item.message.role === 'assistant' && !isCurrentAssistantMessageStreaming);

                            return (
                                <div key={item.message.id ?? index} className="mt-5">
                                    <AIChatMessageCard
                                        message={item.message}
                                        references={item.sources}
                                        isComplete={isComplete}
                                    />
                                </div>
                            );
                        })}

                        {/* Show confirmation cards at the end of the chat */}
                        {pendingConfirmations.map((confirmation) => {
                            const onConfirm = () =>
                                setPendingConfirmations((prev) => prev.filter((c) => c.id !== confirmation.id));
                            const onCancel = () =>
                                setPendingConfirmations((prev) => prev.filter((c) => c.id !== confirmation.id));

                            if (confirmation.type === 'create_page') {
                                return (
                                    <div key={confirmation.id} className="mt-2">
                                        <AIToolConfirmationCard
                                            type={confirmation.type}
                                            data={confirmation.data}
                                            onConfirm={onConfirm}
                                            onCancel={onCancel}
                                        />
                                    </div>
                                );
                            }

                            if (confirmation.type === 'create_page_with_content') {
                                return (
                                    <div key={confirmation.id} className="mt-2">
                                        <AIToolConfirmationCard
                                            type={confirmation.type}
                                            data={confirmation.data}
                                            onConfirm={onConfirm}
                                            onCancel={onCancel}
                                        />
                                    </div>
                                );
                            }

                            if (confirmation.type === 'rewrite_page_content') {
                                return (
                                    <div key={confirmation.id} className="mt-2">
                                        <AIToolConfirmationCard
                                            type={confirmation.type}
                                            data={confirmation.data}
                                            onConfirm={onConfirm}
                                            onCancel={onCancel}
                                        />
                                    </div>
                                );
                            }

                            return null;
                        })}

                        {/* Show generating indicator at the bottom */}
                        {showGenerating && <AIGeneratingIndicator />}
                    </div>
                </div>
            ) : (
                <>
                    <div className="text-2xl font-bold px-3 mb-5 text-center">
                        {t('ai.chat.initial.greeting', { name: user?.data.firstName })}
                    </div>
                    {/* Show generating indicator in empty state */}
                    {showGenerating && <AIGeneratingIndicator />}
                </>
            )}

            <ScrollToBottomButton
                onClick={() => {
                    scrollRef.current?.scrollTo({ top: ref.current?.scrollHeight, behavior: 'smooth' });
                    setScrollStick(true); // Hide button after clicking
                }}
                isVisible={!scrollStick}
            />

            <div className="dark:bg-saga-gray-1000 pb-5 pt-3 px-3">
                {status === 'error' && <AIStatusBanner status={status} error={error} className="mb-0" />}
                <div className="border border-saga-gray-250 dark:border-saga-gray-800 bg-saga-gray-100 dark:bg-saga-gray-900 rounded-md">
                    <div className="mt-1">
                        <AISourcesList
                            sources={sources}
                            onItemRemoved={(id) => {
                                setSources(sources.filter((source) => source.id !== id));
                                track('click-on-ai-chat-button', { source: mode });
                            }}
                        />
                    </div>
                    <AIPromptInput
                        id={inputId}
                        placeholder={t('ai.ask_ai_anything_dotted')}
                        sources={sources}
                        text={text}
                        error={error}
                        setText={setText}
                        status={status}
                        showIcon={false}
                        onUpdateSources={setSources}
                        onSubmit={(text) => {
                            // Allow submission even during loading to interrupt
                            if (text || status === 'loading') {
                                onSend(text || '', sources);
                            }
                        }}
                        onCancel={() => {
                            if (stop && typeof stop === 'function') {
                                track('ai-stop-generation', { source: mode });
                                stop();
                            }
                        }}
                        onRetry={() => {
                            const lastUserPrompt = messages.find((m) => m.role === 'user');
                            if (lastUserPrompt) {
                                generateAIText({ prompt: getPromptText(lastUserPrompt), references: sources });
                            }
                        }}
                        onSourcesVisible={(visible) => {
                            if (!visible) focusInputField();
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default AIChatScreen;
